import { useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { ErrorAlert, InfoAlert, SuccessAlert } from "@shared/ui/alerts";
import { InputController } from "@shared/ui/form";
import { GetWeekdaysOfMonth } from "@shared/utils/getWeekDaysOfMonth";
import {
  validateTimesheetWeekByProject,
  validateTimesheetMonthByProject,
} from "../api/timeEntryApi";
import { useProjectIssues } from "../hooks/useProjectIssues";
import { useChargeHours } from "../hooks/useChargeHours";
import { useStoredProjectAssignments } from "../hooks/useStoredProjectAssignments";
import { buildAutomaticChargeSchema } from "../schema/automaticChargeSchema";
import { buildAutomaticChargeEntry } from "../lib/timeEntryMappers";
import { TYPE_OF_CHARGE } from "../constants";
import { hasNoProjectOptions } from "../lib/projectAssignments";
import { SelectProjectRegisterHour } from "./SelectProjectRegisterHour";
import { EntitySelect } from "./EntitySelect";
import { UnmatchedAssignmentsAlert } from "./UnmatchedAssignmentsAlert";

import type { AutomaticChargeValues } from "../schema/automaticChargeSchema";

import type { TFunction } from "i18next";

export interface RegisterHourAutomaticDialogProps {
  handleClose: () => void;
  open: boolean;
  notLoading: () => void;
  loading: () => void;
  reloadInitialMissingHour: () => void;
  t: TFunction;
}

export const RegisterHourAutomaticDialog = ({
  handleClose,
  open,
  notLoading,
  loading,
  reloadInitialMissingHour,
  t,
}: RegisterHourAutomaticDialogProps) => {
  const typesOfCharge = [
    { id: TYPE_OF_CHARGE.WEEK, description: t("currentWeekLoad") },
    { id: TYPE_OF_CHARGE.MONTH, description: t("currentMonthLoad") },
  ];

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<AutomaticChargeValues>({ resolver: yupResolver(buildAutomaticChargeSchema(t)) as never });

  const projectId = watch("projectId");
  const typeOfCharge = watch("typeOfCharge");

  const {
    all: listProject,
    staffing,
    projects,
    unmatched,
    supportReference,
  } = useStoredProjectAssignments(open);

  const { data: projectIssues } = useProjectIssues(projectId);
  const issueId = projectIssues?.[0]?.id;

  const onClose = () => handleClose();

  // Avisa si la semana/mes ya fue imputada al cambiar el tipo de carga.
  useEffect(() => {
    if (!projectId) return;

    if (typeOfCharge === TYPE_OF_CHARGE.WEEK) {
      validateTimesheetWeekByProject(projectId).then((data) => {
        if (data.isChargedHourInWeek) {
          onClose();
          reset();
          InfoAlert(t("informationTitle"), t("hourChargedMessage"));
        }
      });
    }

    if (typeOfCharge === TYPE_OF_CHARGE.MONTH) {
      validateTimesheetMonthByProject(projectId).then((data) => {
        if (data.isChargedHourInMonth) {
          onClose();
          reset();
          InfoAlert(t("informationTitle"), t("hourChargedInMonthMessage"));
        }
      });
    }
  }, [typeOfCharge]);

  const chargeHours = useChargeHours();

  const onRegister = (values: AutomaticChargeValues) => {
    onClose();
    loading();
    // `issueId` puede ser undefined si el proyecto no tiene issues (comportamiento
    // preexistente sin guard, ver #2572); se documenta con `!` en vez de forzar un
    // guard nuevo que cambiaría el comportamiento.
    const entry = buildAutomaticChargeEntry(values, {
      issueId: issueId!,
      weekDaysOfMonth: GetWeekdaysOfMonth(),
    });

    chargeHours.mutate(entry, {
      onSuccess: () => {
        notLoading();
        SuccessAlert(t("loadHours"), t("doneSuccessfully", { a: "a" })).then(() =>
          reloadInitialMissingHour()
        );
      },
      onError: (error) => {
        notLoading();
        onClose();
        ErrorAlert(t("errorTitle"), error.response?.data?.message ?? t("internalError")).then();
      },
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit(onRegister)}>
        <DialogTitle>{t("automaticHoursLoading")}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid size={12}>
              <UnmatchedAssignmentsAlert unmatched={unmatched} supportReference={supportReference} t={t} />
              <FormControl fullWidth sx={{ mt: 1 }}>
                <SelectProjectRegisterHour
                  control={control}
                  defaultValue={[]}
                  label={t("project")}
                  name="projectId"
                  listValue={listProject}
                  listStaffing={staffing}
                  listProjects={projects}
                  disabled={hasNoProjectOptions(staffing, projects)}
                />
                {errors.projectId && (
                  <span style={{ fontSize: 12, color: "red" }}>{errors.projectId.message}</span>
                )}
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <EntitySelect
                  control={control}
                  defaultValue={[]}
                  label={t("typeOfLoad")}
                  name="typeOfCharge"
                  listValue={typesOfCharge}
                />
                {errors.typeOfCharge && (
                  <span style={{ fontSize: 12, color: "red" }}>{errors.typeOfCharge.message}</span>
                )}
              </FormControl>
            </Grid>
            <Grid id="hours-field" size={12}>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputController
                  control={control}
                  defaultValue={8}
                  label={t("hoursPerDay")}
                  name="hoursPerDay"
                />
                {errors.hoursPerDay && (
                  <span style={{ fontSize: 12, color: "red" }}>{errors.hoursPerDay.message}</span>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("cancel")}</Button>
          <Button type="submit">{t("load")}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
